import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, BadgeDollarSign, BriefcaseBusiness, Home, Info, Layers3, MessageCircle, Smartphone, X } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import logo from "../assets/wonso.png";
import { useLanguage } from "../i18n/LanguageProvider";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";

const icons = { "/": Home, "/solutions": Layers3, "/business": BriefcaseBusiness, "/pricing": BadgeDollarSign, "/about": Info, "/contact": MessageCircle };

export default function MobileNavigation({ isOpen, onClose, menuButtonRef, navigation }) {
  const { t } = useLanguage();
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const navigatingRef = useRef(false);
  const previousOverflowRef = useRef(null);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    let closeTimer;
    const restoreScroll = () => {
      if (previousOverflowRef.current !== null) {
        document.body.style.overflow = previousOverflowRef.current;
        previousOverflowRef.current = null;
      }
    };
    if (isOpen) {
      navigatingRef.current = false;
      setClosing(false);
      if (!dialog.open) {
        previousOverflowRef.current = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        dialog.showModal();
        closeButtonRef.current?.focus({ preventScroll: true });
      }
    } else if (dialog.open) {
      setClosing(true);
      const finish = () => {
        dialog.close();
        restoreScroll();
        setClosing(false);
        const button = menuButtonRef.current;
        const target = !navigatingRef.current && button?.getClientRects().length
          ? button : document.getElementById("main-content");
        target?.focus({ preventScroll: true });
      };
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      closeTimer = window.setTimeout(finish, reducedMotion ? 0 : 240);
    }
    return () => window.clearTimeout(closeTimer);
  }, [isOpen, menuButtonRef]);

  useEffect(() => () => {
    if (previousOverflowRef.current !== null) {
      document.body.style.overflow = previousOverflowRef.current;
      previousOverflowRef.current = null;
    }
  }, []);

  const navigate = () => {
    navigatingRef.current = true;
    onClose();
  };

  return createPortal(
    <dialog
      ref={dialogRef}
      id="ws-mobile-navigation"
      className={`ws-mobile-drawer${closing ? " is-closing" : ""}`}
      aria-label={t("Menu principal")}
      aria-modal="true"
      data-no-i18n="true"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const controls = dialogRef.current.querySelectorAll("a[href], button:not([disabled])");
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }}
    >
      <div className="ws-mobile-drawer__panel">
        <div className="ws-mobile-drawer__top">
          <Link className="ws-mobile-drawer__brand" to="/" onClick={navigate} aria-label={t("WonyaSoft — Accueil")}>
            <img src={logo} alt="" width="36" height="36" /><span>WonyaSoft</span>
          </Link>
          <button ref={closeButtonRef} className="ws-mobile-drawer__close" type="button" aria-label={t("Fermer le menu")} onClick={onClose}><X size={21} aria-hidden="true" /></button>
        </div>
        <div className="ws-mobile-drawer__intro"><p>{t("Explorer WonyaSoft")}</p><span>{t("Des solutions pour votre quotidien.")}</span></div>
        <nav className="ws-mobile-drawer__navigation" aria-label={t("Navigation mobile")}>
          {navigation.map(([label, route]) => {
            const Icon = icons[route];
            return <NavLink key={route} to={route} end={route === "/"} onClick={navigate}>
              <span className="ws-mobile-drawer__icon"><Icon size={22} strokeWidth={1.8} aria-hidden="true" /></span>
              <span>{t(label)}</span><ArrowRight className="ws-mobile-drawer__arrow" size={17} aria-hidden="true" />
            </NavLink>;
          })}
        </nav>
        <div className="ws-mobile-drawer__footer">
          <div className="ws-mobile-drawer__preferences"><LanguageSwitcher /><ThemeSwitcher /></div>
          <NavLink className="ws-mobile-drawer__app" to="/appmobile" onClick={navigate}><Smartphone size={21} aria-hidden="true" /><span>{t("Nos applications")}</span><ArrowRight size={17} aria-hidden="true" /></NavLink>
        </div>
      </div>
    </dialog>, document.body,
  );
}
